import React from "react";
import styled from "styled-components";
import ClipboardIcon from "components/svg/clipboard.svg";
import Tooltip from "components/Tooltip";

/**
 * Usage:
 *
 *     <CopyInputToClipboard>
 *       <input value="foo"/>
 *     </CopyInputToClipboard>
 */

const AREA = 40;

const Copy = styled.div`
  position: relative;

  input {
    padding-right: ${AREA}px;
    text-overflow: ellipsis;
  }

  > *:first-child:not(input) {
    margin-right: ${AREA}px;
  }
`;

const ClipboardArea = styled.div`
  position: absolute;
  width: ${AREA}px;
  bottom: 0;
  top: 0;
  right: 0;

  input + & {
    top: 10px;
  }
`;

const TransparentClipboardButton = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  height: 100%;
  justify-content: center;
  padding-bottom: 2px;
  width: 100%;
`;

const PrimaryClipboardButton = styled(TransparentClipboardButton)`
  ${p => `
    background-color: ${p.theme.PrimaryButton.backgroundColor};

    svg path {
      fill: ${p.theme.PrimaryButton.color};
    }

    &:hover {
      background-color: ${p.theme.PrimaryButton.hover.backgroundColor};
    }
  `};
`;

const COPY_TEXT = "Copy to clipboard";
const COPIED_TEXT = "Copied!";

export const copyToClipboardFromElement = element => {
  if (!element) return false;
  if (/input|textarea/i.test(element.tagName)) {
    element.select();
    document.execCommand("copy");
    element.blur();
  } else {
    const range = document.createRange();
    range.selectNodeContents(element);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand("copy");
  }
  return true;
};

export default class CopyInputToClipboard extends React.Component {
  state = {
    text: COPY_TEXT,
  };

  onMouseLeave = () => {
    this.setState({ text: COPY_TEXT });
  };

  copyToClipboard = () => {
    const input = this.inputRef; // input DOM node
    if (copyToClipboardFromElement(input)) {
      this.setState({ text: COPIED_TEXT });
    }
  };

  render() {
    {
      /* access point to the input DOM node which we'll need for copying text
       * to the clipboard
       *
       * TODO assumes the child prop is a styled component.
       * */
    }
    const childProps = {
      ref: e => {
        this.inputRef = e;
      },
    };
    const { text } = this.state;
    const { tooltipPosition, withPrimaryButton } = this.props;
    const ClipboardButton = withPrimaryButton
      ? PrimaryClipboardButton
      : TransparentClipboardButton;
    return (
      <Copy>
        {/* input element */}
        {React.cloneElement(this.props.children, { ...childProps })}

        <ClipboardArea>
          {/* copy to clipboard icon */}
          <Tooltip text={text} position={tooltipPosition || "bottom"}>
            <ClipboardButton
              onClick={this.copyToClipboard}
              onMouseLeave={this.onMouseLeave}
            >
              <ClipboardIcon width="20" height="20" />
            </ClipboardButton>
          </Tooltip>
        </ClipboardArea>
      </Copy>
    );
  }
}
